import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { isEmbed } from '@/config/embed';
import { getAppUrl, getStripePublishableKey } from '@/config/env';
import { EXTERNAL_URLS, INTERNAL_ROUTES, openExternalUrl } from '@/constants/urls';
import { useMembershipDialog } from '@/contexts/MembershipDialogContext';

export const MEMBERSHIP_SOURCES = ['ballot_cta', 'account_menu', 'settings', 'signup'] as const;
export type MembershipSource = (typeof MEMBERSHIP_SOURCES)[number];

export const membershipPath = (source: MembershipSource) =>
  `${INTERNAL_ROUTES.MEMBERSHIP}?source=${source}`;

/**
 * Opens the support sheet over the current screen. A partner embed can't reach the membership API
 * (it isn't on the partners APIM product), so there it opens VoteMate's own app in a new tab.
 * Elsewhere, a build without a Stripe publishable key can't take payments, so it keeps the
 * donation page.
 */
export function useOpenMembership() {
  const navigate = useNavigate();
  const dialog = useMembershipDialog();

  return useCallback(
    (source: MembershipSource) => {
      if (isEmbed()) {
        openExternalUrl(`${getAppUrl()}${membershipPath(source)}`);
        return;
      }
      if (!getStripePublishableKey()) {
        openExternalUrl(EXTERNAL_URLS.DONATE);
        return;
      }
      // Outside the provider (the sign-up modal), the /membership route opens it instead.
      if (dialog) dialog.openMembership({ source });
      else navigate(membershipPath(source));
    },
    [dialog, navigate]
  );
}
